import React from 'react';
import PropTypes from 'prop-types';
import { ERROR_TEXT_NO_DEFAULT_VALUE } from 'langs/en/errors';
import { DIRECTIONS } from 'utils/constants';
import { generateHTMLIdFromKeyPath } from 'utils/idUtils';
import SelectAttribute from 'components/SelectAttribute';
import CheckBoxAttribute from 'components/Fields/CheckBoxAttribute';
import TextAttribute from 'components/Fields/TextAttribute';
import RadioGroupContainer from 'components/RadioGroupContainer';
import RadioButtonAttribute from 'components/Fields/RadioButtonAttribute';
import InputContainer from '../../InputContainer';
import TypesContainer from '../TypesContainer';
import DirectionButton from './components/DirectionButton';

const FieldEditorBoolean = (props) => {
  const { keyPath, fieldValidityChanged, onFieldValueChange, onFieldUIStateChange, values, uiState, reorderDropdown } = props;
  const { editors: { radioButtonConfig: { defaultOption, options, optionsOrder } } } = values;
  const orderedKeys = (optionsOrder && optionsOrder.length) ? optionsOrder : Object.keys(options);

  const validateAndChangeDefaultOption = (kp, value) => {
    onFieldValueChange(kp, value);
    if (values.hidden && !value.length) {
      fieldValidityChanged(kp, false, ERROR_TEXT_NO_DEFAULT_VALUE);
    } else {
      fieldValidityChanged(kp, true);
    }
  };

  const onChangeOptionLabel = (kp, value) => {
    onFieldValueChange(kp, value);
  };

  const onChangeShouldDisplay = (kp, shouldDisplay) => {
    onFieldValueChange(kp, shouldDisplay);
    if (shouldDisplay === false) {
      const optKey = kp[kp.length - 2];
      const optValue = options[optKey] && options[optKey].value;
      if (optValue && defaultOption === optValue) {
        onFieldValueChange([...keyPath, 'radioButtonConfig', 'defaultOption'], '');
      }
    }
  };

  const onToggleAll = (kp, shouldDisplay) => {
    orderedKeys.forEach((optKey) => {
      onChangeShouldDisplay([...keyPath, 'radioButtonConfig', 'options', optKey, 'shouldDisplay'], shouldDisplay);
    });
  };

  const currentLayout = values.editors.radioButtonConfig.layout || 'vertical';
  const onLayoutChange = (kp, field) => {
    onFieldValueChange([...kp, 'radioButtonConfig', 'layout'], field);
  };

  const selectOptionsObject = orderedKeys.reduce((acc, curr) => {
    const objAtCurr = options[curr];
    if (objAtCurr && objAtCurr.shouldDisplay !== false) {
      acc[objAtCurr.value] = objAtCurr.label;
    }
    return acc;
  }, {});

  const allSelected = orderedKeys.every((k) => options[k] && options[k].shouldDisplay !== false);

  return (
    <div className="col mt-2">
      <div className="row mb-4">
        <div className="col-sm-6">
          <TypesContainer
            keyPath={keyPath}
            onFieldValueChange={onFieldValueChange}
            values={values}
            radioGroupName="Field Type"
            uiState={uiState.type}
            isDropdownOrRadio
          />
        </div>
        <div className="col-sm-6">
          <InputContainer
            label="Default Value"
            keyPath={[...keyPath, 'radioButtonConfig', 'defaultOption']}
            uiState={uiState.editors.radioButtonConfig.defaultOption}
          >
            <SelectAttribute
              keyPath={[...keyPath, 'radioButtonConfig', 'defaultOption']}
              onFieldValueChange={validateAndChangeDefaultOption}
              onFieldUIStateChange={onFieldUIStateChange}
              options={selectOptionsObject}
              value={defaultOption}
            />
          </InputContainer>
        </div>
      </div>

      <div className="row mb-2">
        <div className="col-12">
          <p className="font-weight-bold mb-1">Values to Include</p>
          <table className="table bg-white">
            <thead>
              <tr>
                <th scope="col" className="align-top">
                  <CheckBoxAttribute
                    keyPath={[...keyPath, 'radioButtonConfig', 'toggle-all']}
                    onCheckBoxChange={onToggleAll}
                    value={allSelected}
                  />
                </th>
                <th className="w-25" scope="col">Value</th>
                <th className="w-50" scope="col">Display Name</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {orderedKeys.map((optKey, idx) => {
                const opt = options[optKey];
                return (
                  <tr key={optKey}>
                    <td className="align-middle">
                      <CheckBoxAttribute
                        value={opt.shouldDisplay !== false}
                        keyPath={[...keyPath, 'radioButtonConfig', 'options', optKey, 'shouldDisplay']}
                        onCheckBoxChange={onChangeShouldDisplay}
                        className="position-relative"
                      />
                    </td>
                    <td className="align-middle">{opt.value.charAt(0).toUpperCase() + opt.value.slice(1)}</td>
                    <td className="align-middle">
                      <InputContainer
                        keyPath={[...keyPath, 'radioButtonConfig', 'options', optKey, 'label']}
                        uiState={(uiState.editors.radioButtonConfig.options && uiState.editors.radioButtonConfig.options[optKey]) || {}}
                      >
                        <TextAttribute
                          keyPath={[...keyPath, 'radioButtonConfig', 'options', optKey, 'label']}
                          onFieldValueChange={onChangeOptionLabel}
                          onFieldUIStateChange={onFieldUIStateChange}
                          value={opt.label}
                          id={`${optKey}_option-label`}
                        />
                      </InputContainer>
                    </td>
                    <td className="align-middle">
                      <div className="float-right">
                        <DirectionButton
                          direction={DIRECTIONS.UP}
                          id={generateHTMLIdFromKeyPath([...keyPath, 'radioButtonConfig', 'options', optKey, DIRECTIONS.UP])}
                          onClickHandler={() => reorderDropdown([...keyPath, 'radioButtonConfig', 'optionsOrder'], optKey, DIRECTIONS.UP)}
                          disabled={idx === 0}
                        />
                        <DirectionButton
                          direction={DIRECTIONS.DOWN}
                          id={generateHTMLIdFromKeyPath([...keyPath, 'radioButtonConfig', 'options', optKey, DIRECTIONS.DOWN])}
                          onClickHandler={() => reorderDropdown([...keyPath, 'radioButtonConfig', 'optionsOrder'], optKey, DIRECTIONS.DOWN)}
                          disabled={idx === orderedKeys.length - 1}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row mb-2">
        <div className="col-12">
          <RadioGroupContainer
            radioGroupName="UI Layout"
            uiState={(uiState.editors && uiState.editors.radioButtonConfig && uiState.editors.radioButtonConfig.layout) || {}}
          >
            <RadioButtonAttribute
              field="vertical"
              keyPath={keyPath}
              label="Stacked"
              onRadioButtonChange={onLayoutChange}
              radioGroupName="UI Layout"
              value={currentLayout === 'vertical'}
              stacked={false}
            />
            <RadioButtonAttribute
              field="horizontal"
              keyPath={keyPath}
              label="Side-by-Side"
              onRadioButtonChange={onLayoutChange}
              radioGroupName="UI Layout"
              value={currentLayout === 'horizontal'}
              stacked={false}
            />
          </RadioGroupContainer>
          <p className="text-muted small mt-1">Select how the radio fields appear to the end user.</p>
        </div>
      </div>
    </div>
  );
};

FieldEditorBoolean.propTypes = {
  values: PropTypes.shape({
    editors: PropTypes.shape({
      radioButtonConfig: PropTypes.shape({
        defaultOption: PropTypes.string.isRequired,
        options: PropTypes.object.isRequired,
        optionsOrder: PropTypes.array,
        layout: PropTypes.string,
      }),
    }).isRequired,
    flags: PropTypes.array.isRequired,
    hidden: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    type: PropTypes.string.isRequired,
    uiOrder: PropTypes.number,
    __typename: PropTypes.string,
  }).isRequired,
  uiState: PropTypes.object.isRequired,
  keyPath: PropTypes.array.isRequired,
  fieldValidityChanged: PropTypes.func.isRequired,
  onFieldValueChange: PropTypes.func.isRequired,
  onFieldUIStateChange: PropTypes.func.isRequired,
  reorderDropdown: PropTypes.func.isRequired,
};

export default function FieldBooleanConfigure(props) {
  return <FieldEditorBoolean {...props} />;
}
